/**
 * Composer.VoiceButton — voice-note recorder toggle (June 1, 2026).
 *
 * Purely presentational. The recording lifecycle (MediaRecorder +
 * upload + attachment threading) lives in the parent so this primitive
 * stays in ``@ando/ui`` and the chat feature module owns the side
 * effects.
 *
 * Two visual states:
 *   - **idle** → mic icon, ink-3, faint hover lift
 *   - **recording** → red-orange ring + duration ``mm:ss`` + Stop icon
 *
 * Earlier iterations did Web Speech transcription inline. Replaced
 * because the user wanted an audio MESSAGE (replayable artifact) not a
 * speech-to-text shortcut — the audio rides through the chat-file
 * attachment pipeline now and the BE keeps the MinIO blob.
 *
 * Accessibility:
 *   - ``aria-pressed`` mirrors ``recording``
 *   - ``aria-label`` / ``title`` describe both states explicitly
 *   - Pulse ring respects ``prefers-reduced-motion``
 */

import * as React from "react";
import { Mic, Square } from "lucide-react";
import { cn } from "../../lib/cn";

export interface ComposerVoiceButtonProps {
  /** True while a recording is in progress (parent owns the state). */
  recording: boolean;
  /** Elapsed seconds since recording started (parent ticks this). */
  durationSec: number;
  /**
   * Toggle handler. Parent decides: idle → start mic, recording →
   * stop + flush blob into the attachment pipeline.
   */
  onToggle: () => void;
  /** Disable interaction (e.g. while the previous note is uploading). */
  disabled?: boolean;
  /** True when the browser exposes no MediaRecorder / mic API at all. */
  unsupported?: boolean;
  className?: string;
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const ComposerVoiceButton = React.forwardRef<
  HTMLButtonElement,
  ComposerVoiceButtonProps
>(({ recording, durationSec, onToggle, disabled, unsupported, className }, ref) => {
  const isDisabled = disabled || unsupported;
  const label = unsupported
    ? "Microfono non supportato dal browser"
    : recording
      ? `Interrompi registrazione (${formatDuration(durationSec)})`
      : "Registra un messaggio vocale";

  return (
    <button
      ref={ref}
      type="button"
      onClick={isDisabled ? undefined : onToggle}
      disabled={isDisabled}
      aria-label={label}
      aria-pressed={recording}
      title={label}
      className={cn(
        // 32px tap target (WCAG 2.5.8 with 6px ``::before`` hit-area
        // expansion). The wider recording state animates via padding
        // only — no width transition — so adjacent buttons stay put.
        "relative inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg",
        "before:absolute before:inset-[-6px] before:content-['']",
        "cursor-pointer border-none bg-transparent text-ink-3",
        "transition-[color,background-color,transform] duration-200 ease-smooth",
        "hover:bg-black/[0.045] hover:text-ink-1",
        "active:scale-[0.96]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
        recording
          ? // Live state — orange halo + ink-1 timer text. Earlier draft
            // used ``text-ando-orange`` on ``bg-ando-orange/[0.12]``
            // which fell under 4.5:1 contrast at 11px.
            "w-[72px] bg-ando-orange/[0.14] text-ink-1"
          : "w-8",
        isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
        className,
      )}
    >
      {recording ? (
        <>
          {/* Soft pulse ring — never goes red (we're recording, not in
              alarm). ``motion-safe`` so it falls back to a static ring
              when the OS asks for reduced motion. */}
          <span
            aria-hidden="true"
            className="motion-safe:absolute motion-safe:inset-0 motion-safe:animate-ping motion-safe:rounded-lg motion-safe:ring-2 motion-safe:ring-ando-orange/35"
          />
          <Square
            size={11}
            strokeWidth={2.4}
            fill="currentColor"
            className="text-ando-orange"
            aria-hidden="true"
          />
          <span className="font-mono text-[12px] text-ink-1 tabular-nums leading-none">
            {formatDuration(durationSec)}
          </span>
        </>
      ) : (
        <Mic size={14} strokeWidth={1.85} aria-hidden="true" />
      )}
    </button>
  );
});
ComposerVoiceButton.displayName = "Composer.VoiceButton";
